import { tsoPqTheme } from '@assets/style/customTsoPqtheme';
import TypographyTsoPq from '@components/Typography/TypographyTsoPq';
import { faChartLine } from '@fortawesome/free-solid-svg-icons';
import { GridCellParams, GridColDef, GridRowId } from '@mui/x-data-grid-pro';
import { StyledDataGrid } from '@pages/Masterdata/StyledGridPageComponents';
import {
  StyledCellHeader,
  StyledCellValue,
  StyledCellWrapper,
  StyledChartIcon,
  StyledRowHeaderCell,
  StyledRowHeaderSubtitle,
  TsoIentifier,
} from '@pages/TrendIdentification/TrendIdentificationStyles';
import { useGetMeasurementParameterGetAllQuery } from '@redux/apiSlices/measurementParameterApi';
import { useLazyGetTrendGetTrendValuesQuery } from '@redux/apiSlices/trendApi';
import { useGetTsoGetAllQuery } from '@redux/apiSlices/tsoApi';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { setSnackbar } from '@redux/slices/snackbarSlice';
import {
  addTrendChartData,
  removeTrendChartData,
  SelectedCell,
  selectSelectedCells,
  selectSelectedDeviceDetails,
  selectTrendData,
  setSelectedCells,
  TrendChartEntry,
} from '@redux/slices/trendIdentificationSlice';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

type Row = {
  id: number;
  rowTitle: string;
  rowSubtitle?: string;
  total: number | undefined;
  isSpacer?: boolean;
  isTotalRow?: boolean;
  gridData: GridData[];
};

type GridData = {
  columnName: string;
  columnValue: number | undefined | null;
};

const DataGrid = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const selectedCells = useAppSelector(selectSelectedCells);

  const [triggerGetTrendValues] = useLazyGetTrendGetTrendValuesQuery();
  const { data: measurementParametersRaw } = useGetMeasurementParameterGetAllQuery();
  const { data: allTsos, isSuccess } = useGetTsoGetAllQuery();

  const tsoMap: Record<number, string> = {};

  if (isSuccess && allTsos?.externalDtos) {
    for (const tso of allTsos.externalDtos) {
      if (tso.id != null && tso.abbreviation) {
        tsoMap[tso.id] = tso.abbreviation;
      }
    }
  }

  const [gridData, setGridData] = useState<Row[]>([]);
  const [activeCells, setActiveCells] = useState<SelectedCell[]>(selectedCells);

  const measurementParameters = measurementParametersRaw?.externalDtos ?? [];
  const trendData = useAppSelector(selectTrendData);
  const deviceDetails = useAppSelector(selectSelectedDeviceDetails);

  const MAX_SELECTED_CELLS = 6;

  const isCellSelected = (id: GridRowId, field: string) =>
    activeCells.some((cell) => cell.id === id && cell.field === field);

  const handleCellSelection = async (params: GridCellParams) => {
    const measurementDeviceId = params.row.measurementDeviceId;

    try {
      const response = await triggerGetTrendValues({
        measurementDeviceId,
        categoryPathWithoutPhase: params.field,
      }).unwrap();

      const mappedResponse: TrendChartEntry[] = response
        .filter((entry) => entry.measurementParameterId != null)
        .map((entry) => ({
          measurementParameterId: entry.measurementParameterId!,
          trendValues:
            entry.trendValues?.map((tv) => ({
              weekStart: tv.weekStart!,
              value: tv.value!,
            })) ?? [],
        }));

      dispatch(addTrendChartData({ measurementDeviceId, response: mappedResponse }));

      setActiveCells((prev) => [
        ...prev,
        {
          id: params.id,
          field: params.field,
          measurementDeviceId,
        },
      ]);
    } catch (error) {
      console.error('Trend values error:', measurementDeviceId, params.field, error);
    }
  };

  const showMaxCellsError = () => {
    dispatch(
      setSnackbar({
        open: true,
        type: 'error',
        textContent: t('trednIdentification.maximumNumberOfCells.error'),
        copyToClipboard: false,
        requestID: undefined,
      })
    );
  };

  const handleCellDeselection = (params: GridCellParams) => {
    const measurementDeviceId = params.row.measurementDeviceId;

    setActiveCells((prev) => prev.filter((cell) => !(cell.id === params.id && cell.field === params.field)));

    dispatch(
      removeTrendChartData({
        measurementDeviceId,
        categoryPath: params.field,
        allParameters: measurementParameters,
      })
    );
  };

  const handleCellClick = async (params: GridCellParams) => {
    if (params.field === 'title') return;

    const { id, field } = params;

    const isAlreadySelected = activeCells.some((cell) => cell.id === id && cell.field === field);

    if (isAlreadySelected) {
      handleCellDeselection(params);
      return;
    }

    if (activeCells.length >= MAX_SELECTED_CELLS) {
      showMaxCellsError();
      return;
    }

    await handleCellSelection(params);
  };

  const generateGridData = () => {
    if (!deviceDetails) {
      return;
    }

    const map = new Map();

    for (const item of trendData) {
      const { measurementDeviceId, measurementParameterCategoryWithoutPhase, numberOfTrends } = item;

      if (!map.has(measurementDeviceId)) {
        const deviceInfo = deviceDetails[measurementDeviceId as keyof typeof deviceDetails];

        const device = deviceInfo?.measurementDeviceName ?? '';
        const substation = deviceInfo?.substationName ?? '';
        const measurementPoint = deviceInfo?.measurementPointName ?? '';
        const tsoId = deviceInfo?.tsoId ?? Number.NaN;

        map.set(measurementDeviceId, {
          id: map.size + 1,
          measurementDeviceId,
          gridData: [],
          rowTitle: device,
          rowSubtitle: `${substation} > ${measurementPoint}`,
          tsoId,
        });
      }

      const entry = map.get(measurementDeviceId);
      if (entry) {
        entry.gridData.push({
          columnName: measurementParameterCategoryWithoutPhase,
          columnValue: numberOfTrends,
        });
      }
    }

    setGridData(Array.from(map.values()));
  };

  const getColumnWithoutDuplicates = (rows: Row[]) => {
    const columns = rows.flatMap((row) => row.gridData);
    const uniqueColumns = columns.filter(
      (column, index, self) => index === self.findIndex((col) => column.columnName === col.columnName)
    );

    return uniqueColumns;
  };

  const displayCellValue = (row: Row, column: string): number | undefined | null => {
    const value = row.gridData.find((item) => item.columnName === column)?.columnValue;
    return value;
  };

  const generateDataColumns = (column: string): GridColDef => ({
    field: column,
    headerName: t(column),
    minWidth: 140,
    flex: 1,
    headerClassName: 'customBackgroundHeader',
    resizable: false,
    sortable: false,
    renderCell: (params) => {
      const displayValue = displayCellValue(params.row, column);
      return (
        <>
          {params.row.gridData.length < 1 ? (
            <></>
          ) : (
            <StyledCellWrapper>
              <StyledCellValue typographyType="bodyTextSM">{displayValue}</StyledCellValue>
              {isCellSelected(params.id, params.field) && (
                <StyledChartIcon icon={faChartLine} buttonType="transparentPrimary" sizeVariant="m" />
              )}
            </StyledCellWrapper>
          )}
        </>
      );
    },
  });

  const columns: GridColDef[] = [
    {
      field: 'title',
      headerName: '',
      renderHeader: () => undefined,
      width: 300,
      headerClassName: () => 'customBackgroundHeader',
      resizable: false,
      sortable: false,
      renderCell: (params: any) => {
        const tsoAbbreviation = tsoMap[params.row.tsoId] ?? 'default';

        return (
          <StyledRowHeaderCell>
            <StyledCellHeader>
              <TsoIentifier tsoAbbreviation={tsoAbbreviation} />
              {params.row.rowTitle && (
                <TypographyTsoPq typographyType="labelMDBold">{params.row.rowTitle}</TypographyTsoPq>
              )}
            </StyledCellHeader>
            {params.row.rowSubtitle && (
              <StyledRowHeaderSubtitle typographyType="labelMD">{params.row.rowSubtitle}</StyledRowHeaderSubtitle>
            )}
          </StyledRowHeaderCell>
        );
      },
    },
    ...getColumnWithoutDuplicates(gridData).map((row) => generateDataColumns(row.columnName)),
  ];

  useEffect(() => {
    generateGridData();
  }, [trendData, deviceDetails]);

  useEffect(() => {
    dispatch(setSelectedCells(activeCells));
  }, [activeCells, dispatch]);

  return (
    <StyledDataGrid
      initialState={{
        pinnedColumns: { left: ['title'] },
      }}
      rows={gridData}
      columns={columns}
      disableColumnReorder
      disableColumnPinning
      disableRowSelectionOnClick
      hideFooterSelectedRowCount
      showCellVerticalBorder
      hideFooter
      columnHeaderHeight={37}
      onCellClick={handleCellClick}
      getRowId={(row) => row.id}
      disableColumnMenu
      getCellClassName={(params) =>
        activeCells.some((cell) => cell.id === params.id && cell.field === params.field) ? 'selected-cell' : ''
      }
      sx={{
        maxHeight: 5 * 57 + 37,
        overflowY: 'auto',
        '& .MuiDataGrid-cell': {
          display: 'flex',
          alignItems: 'center',
          pt: tsoPqTheme.spacingValues.componentSpacing.spacing_component_gap_8,
          pb: tsoPqTheme.spacingValues.componentSpacing.spacing_component_gap_8,
        },
        "& .MuiDataGrid-cell[data-field='title']": {
          backgroundColor: tsoPqTheme.palette.table.color_other_table_header,
        },
        "& .MuiDataGrid-cell[data-field='title']:hover": {
          backgroundColor: tsoPqTheme.palette.table.color_other_table_header,
        },
        "& .MuiDataGrid-cell:not(.selected-cell):not([data-field='title']):hover": {
          backgroundColor: tsoPqTheme.palette.background.neutral.color_neutral_background_bg_ntrl_enabled_lt,
          cursor: 'pointer',
        },
        '& .MuiDataGrid-row:hover': {
          backgroundColor: 'transparent',
        },
        '& .selected-cell': {
          backgroundColor: tsoPqTheme.palette.background.primary.color_primary_background_bg_prim,
        },
      }}
    />
  );
};

export default DataGrid;
